import {$qs, $qsAll, stripHtml} from '../../@shared/ts/dom';
import {type PaymentData} from '../../@shared/ts/paymentAttempt';
import {retrieveTransactionId} from './transaction';
import {type PeachPayCheckoutData, type TokenizeFunctions} from './types';

declare const peachpay_checkout_data: PeachPayCheckoutData;

function getCheckoutForm() {
	return document.querySelector<HTMLFormElement>('form.checkout') ?? document.querySelector<HTMLFormElement>('#order_review');
}

function onCheckoutUpdate(callback: () => (Promise<void> | void)) {
	// https://github.com/woocommerce/woocommerce/blob/d30c54ef846b086b96278375b71f7c379d9aa8e8/assets/js/frontend/checkout.js#L335
	jQuery(document.body).on('updated_checkout', async () => {
		await callback();
	});
}

async function onCheckoutGatewayChange(callback: (gatewayId: string) => (Promise<void> | void)): Promise<void> {
	// Make sure to load any currently selected buttons
	const $checkoutForm = getCheckoutForm();
	if (!$checkoutForm) {
		return;
	}

	const formData = new FormData($checkoutForm);
	let currentPaymentMethod = formData.get('payment_method') as string;
	await callback(currentPaymentMethod);

	observeCheckoutHTML(async () => {
		const $checkoutForm = getCheckoutForm();
		if (!$checkoutForm) {
			return;
		}

		const formData = new FormData($checkoutForm);
		const nextPaymentMethod = formData.get('payment_method') as string;
		if (nextPaymentMethod === currentPaymentMethod) {
			return;
		}

		currentPaymentMethod = nextPaymentMethod;
		await callback(currentPaymentMethod);
	});
}

function onCheckoutSubmitManual($checkoutForm: HTMLFormElement, paymentMethods: string[], callback: (gatewayId: string) => Promise<boolean> | boolean) {
	onCheckoutUpdate(() => {
		const stripeForm = $checkoutForm.querySelector('#pp-stripe-form-submit');
		if (stripeForm) {
			jQuery(stripeForm).on('submit', async event => {
				event.preventDefault();
				event.stopPropagation();

				const placeOrder = $checkoutForm.querySelector('#place_order');
				if (placeOrder) {
					jQuery(placeOrder).trigger('click');
				}
			});
		}
	});

	jQuery($checkoutForm).on('click', async event => {
		if (!jQuery(event.target).closest('#place_order').length) {
			return;
		}

		try {
			const formData = new FormData($checkoutForm);
			const paymentMethod = formData.get('payment_method') as string;

			if (!paymentMethod || !paymentMethods.includes(paymentMethod)) {
				return;
			}

			jQuery($checkoutForm).block({
				message: null,
				overlayCSS: {
					background: '#fff',
					opacity: 0.6,
				},
			});

			// We do prevent default here to make sure we do not break other
			// non peachpay gateways
			event.preventDefault();
			event.stopPropagation();

			const shouldSubmit = await callback(paymentMethod);

			if (shouldSubmit) {
				jQuery($checkoutForm).trigger('submit');
			} else {
				jQuery(document.body).trigger('checkout_error');
				unblockCheckout($checkoutForm);
			}
		} catch (error) {
			console.error(error);
			jQuery(document.body).trigger('checkout_error');
			unblockCheckout($checkoutForm);
		}
	});
}

function onCheckoutSubmit($checkoutForm: HTMLFormElement, tokenizeFunctions: TokenizeFunctions) {
	const supportedPaymentMethods = Object.keys(tokenizeFunctions);

	// We target the form element for the click to prevent the event listener from being lost when the form html is updated.
	jQuery($checkoutForm).on('click', async event => {
		if (!jQuery(event.target).closest('#place_order').length) {
			return;
		}

		try {
			const formData = new FormData($checkoutForm);
			const gateway = formData.get('payment_method') as string;

			if (!gateway || !supportedPaymentMethods.includes(gateway)) {
				// Selected payment method not relevant to this onClick listener
				return;
			}

			// We do prevent default here to make sure we do not break other
			// non peachpay gateways
			event.preventDefault();
			event.stopPropagation();

			jQuery($checkoutForm).block({
				message: null,
				overlayCSS: {
					background: '#fff',
					opacity: 0.6,
				},
			});

			const tokenizeFunction = await tokenizeFunctions[gateway];

			if (!tokenizeFunction) {
				return;
			}

			if (await tokenizeFunction($checkoutForm, gateway)) {
				jQuery($checkoutForm).trigger('submit');
			} else {
				jQuery(document.body).trigger('checkout_error');
				unblockCheckout($checkoutForm);
			}
		} catch (error) {
			console.error(error);
			jQuery(document.body).trigger('checkout_error');
			unblockCheckout($checkoutForm);
		}
	});
}

function onCheckoutSubmitPaymentData<T extends PaymentData>(type: string, callback: (data: T) => void) {
	window.addEventListener('hashchange', () => {
		const [name, data] = location.hash.split('=');
		if (name !== '#payment_data' || !data) {
			return;
		}

		const json = JSON.parse(atob(decodeURIComponent(data))) as T;
		if (json.type !== type) {
			return;
		}

		history.replaceState('', document.title, window.location.pathname + window.location.search);

		callback(json);
	}, false);
}

type PayPalPaymentPayload = PaymentData & {
	data: {
		id: string;
	};
};

async function submitOrderPayPalViaFetch($form: HTMLFormElement): Promise<PayPalPaymentPayload> {
	const baseUrl = $form.action || window.location.href.replace(/#.*$/, '');
	const url = new URL(baseUrl);
	url.searchParams.set('wc-ajax', 'checkout');

	const formData = new FormData($form);
	const response = await fetch(url.toString(), {
		method: 'POST',
		body: formData,
	});

	if (!response.ok) {
		throw new Error(`Checkout failed (${response.status}). Please try again.`);
	}

	const json = await response.json() as {result?: string; redirect?: string; messages?: string};
	const {result, redirect, messages} = json;

	if (result !== 'success') {
		const messageText = typeof messages === 'string' && messages.trim()
			? messages.trim()
			: retrieveWCCheckoutErrors().join('\n');

		throw new Error(messageText || 'Checkout could not be completed. Please try again.');
	}

	if (!redirect || typeof redirect !== 'string') {
		throw new Error('Invalid checkout response: no redirect.');
	}

	const hashRegex = /#payment_data=(.+)$/;
	const queryRegex = /[?&]payment_data=([^&]+)/;
	const hashExec = hashRegex.exec(redirect);
	const queryExec = queryRegex.exec(redirect);
	const paymentDataEncoded = hashExec?.[1] ?? queryExec?.[1];

	if (!paymentDataEncoded) {
		throw new Error('Invalid checkout response: no payment_data in redirect.');
	}

	const decoded = JSON.parse(atob(decodeURIComponent(paymentDataEncoded))) as PayPalPaymentPayload;

	if (decoded.type !== 'paypal') {
		throw new Error(`Invalid payment data type: expected paypal, got ${decoded.type}.`);
	}

	return decoded;
}

async function submitOrder<T extends PaymentData>(type: string, extraFields: Record<string, string> = {}): Promise<T> {
	return new Promise((res, rej) => {
		let hashChangeHandler: null | (() => void) = null;
		let checkoutSubmitErrorHandler: null | (() => void) = null;

		const removeEvents = () => {
			if (hashChangeHandler) {
				window.removeEventListener('hashchange', hashChangeHandler, false);
			}

			if (checkoutSubmitErrorHandler) {
				jQuery(document.body).off('checkout_error', checkoutSubmitErrorHandler);
			}
		};

		hashChangeHandler = () => {
			const [name, data] = location.hash.split('=');
			if (name !== '#payment_data' || !data) {
				removeEvents();
				rej(new Error('Invalid url hash payment data: ' + location.hash));
				return;
			}

			const json = JSON.parse(atob(decodeURIComponent(data))) as T;

			if (json.type !== type) {
				rej(new Error('Invalid url hash payment data type. Expected: ' + type + ' but got: ' + json.type));
				return;
			}

			// This immediately removes the hash from the URL.
			history.replaceState('', document.title, window.location.pathname + window.location.search);

			removeEvents();
			res(json);
		};

		checkoutSubmitErrorHandler = () => {
			removeEvents();
			rej(retrieveWCCheckoutErrors().join('\n'));
		};

		window.addEventListener('hashchange', hashChangeHandler, false);
		jQuery(document.body).on('checkout_error', checkoutSubmitErrorHandler);

		const $form = getCheckoutForm();
		if (!$form) {
			rej(new Error('Unable to locate checkout form for submission.'));
			return;
		}

		for (const key of Object.keys(extraFields)) {
			const value = extraFields[key];

			if (value === undefined) {
				continue;
			}

			const $input = document.createElement('input');
			$input.type = 'hidden';
			$input.name = key;
			$input.value = value;
			$form.appendChild($input);
		}

		jQuery($form).trigger('submit');
	});
}

function retrieveWCCheckoutErrors(): string[] {
	const errors: string[] = [];
	const $htmlError = $qs('ul.woocommerce-error');
	if ($htmlError) {
		for (const $li of Array.from($htmlError.querySelectorAll('li'))) {
			errors.push(stripHtml($li.innerHTML).trim());
		}
	}

	return errors;
}

function onCheckoutSubmitError($checkoutForm: HTMLFormElement, tokenizeFunctions: TokenizeFunctions, callback: (transactionId: string | null, errors: string[]) => Promise<void> | void) {
	// https://mircian.com/2016/10/12/hooking-woocommerces-js-events/
	// https://github.com/woocommerce/woocommerce/blob/d30c54ef846b086b96278375b71f7c379d9aa8e8/assets/js/frontend/checkout.js#L455
	jQuery(document.body).on('checkout_error', async () => {
		const transactionId = retrieveTransactionId();

		const formData = new FormData($checkoutForm);
		const gateway = formData.get('payment_method') as string;
		const tokenizeFunction = await tokenizeFunctions[gateway];

		if (!gateway || !tokenizeFunction) {
			return;
		}

		await callback(transactionId, retrieveWCCheckoutErrors());
	});
}

function onCheckoutSubmitErrorManual($checkoutForm: HTMLFormElement, gatewayIdList: string[], callback: (transactionId: string | null, errors: string[]) => Promise<void> | void) {
	// https://mircian.com/2016/10/12/hooking-woocommerces-js-events/
	// https://github.com/woocommerce/woocommerce/blob/d30c54ef846b086b96278375b71f7c379d9aa8e8/assets/js/frontend/checkout.js#L455
	jQuery(document.body).on('checkout_error', async () => {
		const transactionId = retrieveTransactionId();

		const formData = new FormData($checkoutForm);
		const gateway = formData.get('payment_method') as string;
		const gatewayExists = gatewayIdList.includes(gateway);

		if (!gateway || !gatewayExists) {
			return;
		}

		await callback(transactionId, retrieveWCCheckoutErrors());
	});
}

/**
 * https://www.notion.so/peachpay/Native-Checkout-Stripe-payment-failures-in-the-confirm-step-result-in-an-infinite-loader-2ad161795f0f42afbb65fef63d95c06c
 *
 * @param $checkoutForm Current checkout form.
 */
function unblockCheckout($checkoutForm?: HTMLFormElement | null) {
	if (!$checkoutForm) {
		$checkoutForm = getCheckoutForm();
		if (!$checkoutForm) {
			throw new Error('Unable to locate checkout form.');
		}
	}

	jQuery($checkoutForm).unblock();

	// Wrap in try catch to avoid errors if $checkoutForm is not what we expect.
	try {
		$checkoutForm?.classList?.remove('processing');
	} catch (e) {
		// On failure, we really can't do much else other than console.error and do a full page reload.
		console.error(e);
		location.reload();
	}
}

function observeCheckoutHTML(callback: () => void) {
	const $checkoutForm = getCheckoutForm();
	if (!$checkoutForm) {
		return;
	}

	const observer = new MutationObserver(callback);
	observer.observe($checkoutForm, {childList: true, subtree: true});
}

function disableGatewayOption(gateway: string) {
	return () => {
		const $option = document.querySelector('li.wc_payment_method.payment_method_' + gateway);

		if (!$option) {
			return;
		}

		try {
			// If this payment gateway was selected, find another gateway to replace it as selected
			if ($option.querySelector<HTMLInputElement>('input[type="radio"]')?.checked) {
				// click the first payment gateway in the list
				document.querySelector<HTMLInputElement>('ul.wc_payment_methods > li:first-child input[type="radio"]')?.click();
			}
		} catch (error) {
			console.error(error);
		}

		$option.remove();
	};
}

function tokenizedMethodSelected(gateway: string): boolean {
	const name = `wc-${gateway}-payment-token`;

	const $radio = $qs<HTMLInputElement>(`input[type="radio"][name="${name}"]:checked`);
	if (!$radio) {
		return false;
	}

	// When the radio button exists but "Use a new method" is selected the value is then "new".
	if ($radio.value === 'new') {
		return false;
	}

	return true;
}

function fieldGetSet(name: string, defaultValue?: () => string) {
	return {
		// eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
		get: () => ((jQuery(`input[name="${name}"],select[name="${name}"]`).val() as string) || defaultValue?.()) || '',
		set(newValue: string) {
			jQuery(`input[name="${name}"],select[name="${name}"]`)
				.val(newValue)
				.trigger('change');
		},
	};
}

function fieldCheckUncheck(name: string) {
	return {
		get: () => $qs<HTMLInputElement>(`input[name="${name}"]`)?.checked ?? false,
		set(checked: boolean) {
			$qs<HTMLInputElement>(`input[name="${name}"]`, $input => {
				$input.checked = checked;
			});
		},
	};
}

function shippingFieldGetSet(partialName: string, defaultValue?: () => string) {
	return {
		get() {
			const differentShipping = fieldCheckUncheck('ship_to_different_address');
			if (differentShipping.get()) {
				return fieldGetSet(`shipping_${partialName}`, defaultValue).get();
			}

			return fieldGetSet(`billing_${partialName}`, defaultValue).get();
		},
		set(value: string) {
			const differentShipping = fieldCheckUncheck('ship_to_different_address');
			if (differentShipping.get()) {
				fieldGetSet(`shipping_${partialName}`).set(value);
				return;
			}

			fieldGetSet(`billing_${partialName}`).set(value);
		},
	};
}

/**
 * Handles refreshing the checkout when a payment method changes to ensure things like
 * fees are always accurate. Only refreshes as needed.
 */
document.addEventListener('DOMContentLoaded', () => {
	let previousPaymentMethod = '';
	let previousRequiredRefresh = false;
	let previousServiceFee: string | undefined = 'false';

	observeCheckoutHTML(() => {
		const $checkoutForm = getCheckoutForm();
		if (!$checkoutForm) {
			return;
		}

		const formData = new FormData($checkoutForm);
		const currentPaymentMethod = formData.get('payment_method') as string;
		if (currentPaymentMethod === previousPaymentMethod) {
			return;
		}

		previousPaymentMethod = currentPaymentMethod;

		const $selectedContainer = $qs(`div.payment_box.payment_method_${currentPaymentMethod}`);
		const currentShouldRefresh = $selectedContainer?.querySelector<HTMLDivElement>('div[data-should-refresh-checkout]')?.dataset?.['shouldRefreshCheckout'];
		const currentServiceFee = $selectedContainer?.querySelector<HTMLDivElement>('div[data-service-fee]')?.dataset?.['serviceFee'];
		if (currentShouldRefresh === 'true' || previousRequiredRefresh || currentServiceFee !== previousServiceFee) {
			previousRequiredRefresh = currentShouldRefresh === 'true';
			previousServiceFee = currentServiceFee;
			jQuery(document.body).trigger('update_checkout', {update_shipping_method: false});
		}
	});
});

const FormFields = {
	billing: {
		email: fieldGetSet('billing_email'),
		phone: fieldGetSet('billing_phone'),
		firstName: fieldGetSet('billing_first_name'),
		lastName: fieldGetSet('billing_last_name'),
		address1: fieldGetSet('billing_address_1'),
		address2: fieldGetSet('billing_address_2'),
		city: fieldGetSet('billing_city'),
		state: fieldGetSet('billing_state', () => peachpay_checkout_data.merchant.defaults.state),
		postcode: fieldGetSet('billing_postcode'),
		country: fieldGetSet('billing_country', () => peachpay_checkout_data.merchant.defaults.country),
	},
	differentShippingAddress: fieldCheckUncheck('ship_to_different_address'),
	shipping: {
		email: shippingFieldGetSet('email'),
		phone: shippingFieldGetSet('phone'),
		firstName: shippingFieldGetSet('first_name'),
		lastName: shippingFieldGetSet('last_name'),
		address1: shippingFieldGetSet('address_1'),
		address2: shippingFieldGetSet('address_2'),
		city: shippingFieldGetSet('city'),
		state: shippingFieldGetSet('state', () => peachpay_checkout_data.merchant.defaults.state),
		postcode: shippingFieldGetSet('postcode'),
		country: shippingFieldGetSet('country', () => peachpay_checkout_data.merchant.defaults.country),
	},
};

function displayTokenizeError(message?: string) {
	if (!message) {
		return;
	}

	const formattedMessage = message
		.split('\n')
		.map(part => part.trim())
		.filter(Boolean)
		.join('<br>');
	const targets = new Set<HTMLElement>();

	// Target only the payment box not hidden.
	$qsAll('.payment_box:not([style*="display:none"]):not([style*="display: none"]) .peachpay-tokenize-error', $el => {
		targets.add($el);
	});

	const selectedGateway = document.querySelector<HTMLInputElement>('input[name="payment_method"]:checked')?.value;
	if (selectedGateway) {
		const selectedGatewayError = document
			.querySelector<HTMLElement>(`.payment_box.payment_method_${selectedGateway} .peachpay-tokenize-error`);
		if (selectedGatewayError) {
			targets.add(selectedGatewayError);
		}
	}

	targets.forEach($el => {
		$el.classList.remove('hide');
		$el.innerHTML = formattedMessage;

		$el.scrollIntoView();
	});

	setTimeout(() => {
		$qsAll('.peachpay-tokenize-error', $el => {
			$el.classList.add('hide');
			$el.innerHTML = '';
		});
	}, 10000);
}

export {
	onCheckoutUpdate,
	onCheckoutGatewayChange,
	onCheckoutSubmit,
	onCheckoutSubmitManual,
	onCheckoutSubmitError,
	onCheckoutSubmitErrorManual,
	onCheckoutSubmitPaymentData,
	submitOrder,
	submitOrderPayPalViaFetch,
	tokenizedMethodSelected,
	observeCheckoutHTML,
	disableGatewayOption,
	unblockCheckout,
	FormFields,
	getCheckoutForm,
	fieldGetSet,
	displayTokenizeError,
};
